// AnswerStyleSelector — shared dropdown for Answer Style (Public Q&A surface + Silo Wars).
// QuestionStyleSelector — sibling for question generation (Silo Wars only).
//
// Both presets share a "Customize…" option that lets the user name their own
// preset and pick a word-count range. Storage is decided by the parent:
//   - Public Q&A: parent saves preset/custom to silo.answer_style_*
//   - Silo Wars: parent stores in localStorage + ships into the challenge payload
//
// No backend round-trip in this component; pure controlled input.

import { useEffect, useState } from "react";
import { CaretDown, Pencil, X } from "@phosphor-icons/react";

export const ANSWER_PRESETS = [
  // Feb 27, 2026 — Source of truth: backend ANSWER_STYLE_PRESETS in
  // routes/battles.py. Keep these in sync. Order = shortest → longest →
  // "no rule" at the bottom (mirrors the Length Bump drawer fans see).
  { id: "one_line",    label: "One liner",     hint: "One single sentence (10-30 words). Best for daily reminders or quick nudges." },
  { id: "one_liner",   label: "One paragraph", hint: "One clean, punchy paragraph (40-80 words). Best for quick takes." },
  { id: "concise",     label: "Concise",       hint: "2-3 short paragraphs (150-300 words). Tight and readable." },
  { id: "standard",    label: "Standard",      hint: "3-5 paragraphs (400-700 words). Balanced depth and flow." },
  { id: "deep_dive",   label: "Deep dive",     hint: "Long-form (800-2,000 words). Best for complex, nuanced questions." },
  { id: "free_choice", label: "Let the AI decide", hint: "No length rule — the AI picks how long the answer should be." },
];

export const QUESTION_PRESETS = [
  { id: "punchy",   label: "Punchy",   hint: "Sharp, focused — 12-20 words." },
  { id: "standard", label: "Standard", hint: "Clear, focused — 25-45 words." },
  { id: "academic", label: "Academic", hint: "Multi-clause, comprehensive — 50-100 words." },
];

function StyleSelector({
  variant,        // "answer" | "question"
  preset,         // current preset id or "custom"
  custom,         // current custom config {name, min_words, max_words} or null
  onChange,       // ({preset, custom}) => void
  testidPrefix = "style-selector",
  compact = false,
}) {
  const presets = variant === "question" ? QUESTION_PRESETS : ANSWER_PRESETS;
  const label = variant === "question" ? "Question style" : "Answer style";
  const [showCustom, setShowCustom] = useState(preset === "custom");
  const [draft, setDraft] = useState(() => ({
    name: custom?.name || "Tight",
    min_words: custom?.min_words ?? (variant === "question" ? 12 : 100),
    max_words: custom?.max_words ?? (variant === "question" ? 30 : 300),
  }));

  useEffect(() => { setShowCustom(preset === "custom"); }, [preset]);

  const pickPreset = (id) => {
    if (id === "custom") {
      setShowCustom(true);
      // Don't fire onChange yet — wait for save in the editor
      return;
    }
    setShowCustom(false);
    onChange({ preset: id, custom: null });
  };

  const saveCustom = () => {
    const min = Math.max(variant === "question" ? 8 : 20, parseInt(draft.min_words, 10) || 50);
    const max = Math.max(min + (variant === "question" ? 5 : 20), parseInt(draft.max_words, 10) || min + 40);
    const name = (draft.name || "Custom").trim().slice(0, 40);
    onChange({ preset: "custom", custom: { name, min_words: min, max_words: max } });
    setShowCustom(false);
  };

  const cancelCustom = () => {
    setShowCustom(false);
    // If preset was already custom, leave it alone. If not, snap back.
    if (preset !== "custom") onChange({ preset: preset || presets[0].id, custom: null });
  };

  // Feb 14, 2026 PM — Delete the saved custom. Snap back to a sensible
  // built-in (Concise for answers, Standard for questions) and wipe the
  // stored custom shape. Parent decides persistence (silo PATCH or
  // localStorage clear).
  const SNAP_BACK_DEFAULT = variant === "question" ? "standard" : "concise";
  const deleteCustom = () => {
    setShowCustom(false);
    onChange({ preset: SNAP_BACK_DEFAULT, custom: null });
    // Reset the draft so reopening "Customize…" starts fresh.
    setDraft({
      name: "Tight",
      min_words: variant === "question" ? 12 : 100,
      max_words: variant === "question" ? 30 : 300,
    });
  };

  const currentLabel = (() => {
    if (preset === "custom" && custom?.name) return `Custom · ${custom.name}`;
    const found = presets.find((p) => p.id === preset);
    return found ? found.label : presets[0].label;
  })();

  return (
    <div data-testid={`${testidPrefix}-${variant}`}>
      <label className={`block text-xs text-[#6B6A68] mb-1.5 ${compact ? "" : "font-medium"}`}>
        {label}
      </label>
      <div className="relative">
        <select
          value={preset === "custom" ? "custom" : (preset || presets[0].id)}
          onChange={(e) => pickPreset(e.target.value)}
          className="appearance-none w-full bg-white border border-[#D8D6D2] rounded-md px-3 py-2 pr-8 text-sm focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]"
          data-testid={`${testidPrefix}-${variant}-select`}
        >
          {presets.map((p) => (
            <option key={p.id} value={p.id}>{p.label}</option>
          ))}
          <option value="custom">{custom?.name ? `Custom · ${custom.name}` : "Customize…"}</option>
        </select>
        <CaretDown size={12} weight="duotone" className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6B6A68] pointer-events-none" />
      </div>

      {/* Preset hint */}
      {preset !== "custom" && (
        <p className="text-[10px] text-[#6B6A68] mt-1 leading-snug">
          {presets.find((p) => p.id === preset)?.hint || presets[0].hint}
        </p>
      )}

      {/* Saved custom preset card — ALWAYS shown when a custom exists,
          regardless of whether Custom is currently the active preset.
          This is how users discover + delete leftover customs they don't
          want to use anymore. Click the body to switch to it; click X to
          delete it. Feb 14, 2026 PM — fix from user feedback. */}
      {custom && !showCustom && (
        <div
          className={`mt-1.5 flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-md border text-[10px] ${
            preset === "custom"
              ? "bg-[#FAF7F2] border-[#E8E5DF] text-[#6B6A68]"
              : "bg-white border-[#E8E5DF] text-[#6B6A68]"
          }`}
        >
          <button
            type="button"
            onClick={() => preset !== "custom" && onChange({ preset: "custom", custom })}
            disabled={preset === "custom"}
            className={`flex-1 min-w-0 truncate text-left ${preset !== "custom" ? "hover:text-[#1C1B1A] cursor-pointer" : "cursor-default"}`}
            data-testid={`${testidPrefix}-${variant}-custom-summary`}
            title={preset === "custom" ? "Active custom preset" : "Switch to this custom preset"}
          >
            {preset === "custom" ? (
              <><strong className="text-[#1C1B1A]">{custom.name}</strong> · {custom.min_words}-{custom.max_words} words</>
            ) : (
              <><span className="opacity-60">Saved:</span> <strong className="text-[#1C1B1A]">{custom.name}</strong> · {custom.min_words}-{custom.max_words} words</>
            )}
          </button>
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={() => setShowCustom(true)}
              className="text-[#4A5D4E] hover:text-[#1C1B1A] flex items-center gap-1"
              data-testid={`${testidPrefix}-${variant}-edit-custom`}
              aria-label="Edit custom preset"
            >
              <Pencil size={10} /> Edit
            </button>
            <button
              type="button"
              onClick={deleteCustom}
              className="text-[#A35757] hover:text-[#7A3F3F] flex items-center gap-1"
              data-testid={`${testidPrefix}-${variant}-delete-custom`}
              title="Delete this custom preset"
              aria-label="Delete custom preset"
            >
              <X size={10} /> Delete
            </button>
          </div>
        </div>
      )}

      {/* Custom editor */}
      {showCustom && (
        <div className="mt-2 p-3 rounded-md bg-[#FAF7F2] border border-[#E8E5DF] space-y-2.5" data-testid={`${testidPrefix}-${variant}-custom-editor`}>
          <div className="flex items-center justify-between gap-2">
            <span className="text-[10px] uppercase tracking-[0.14em] text-[#6B6A68]">Custom preset</span>
            <button
              type="button"
              onClick={cancelCustom}
              className="text-[#6B6A68] hover:text-[#1C1B1A]"
              aria-label="Cancel custom"
            >
              <X size={12} />
            </button>
          </div>
          <div>
            <label className="block text-[10px] text-[#6B6A68] mb-1">Preset name</label>
            <input
              type="text"
              maxLength={40}
              value={draft.name}
              onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
              placeholder="e.g. Tight, Verbose, Tweet-length"
              className="w-full bg-white border border-[#D8D6D2] rounded-md px-2.5 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]"
              data-testid={`${testidPrefix}-${variant}-custom-name`}
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-[10px] text-[#6B6A68] mb-1">Min words</label>
              <input
                type="number"
                min={variant === "question" ? 8 : 20}
                max={2000}
                value={draft.min_words}
                onChange={(e) => setDraft((d) => ({ ...d, min_words: e.target.value }))}
                className="w-full bg-white border border-[#D8D6D2] rounded-md px-2.5 py-1.5 text-xs tabular-nums focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]"
                data-testid={`${testidPrefix}-${variant}-custom-min`}
              />
            </div>
            <div>
              <label className="block text-[10px] text-[#6B6A68] mb-1">Max words</label>
              <input
                type="number"
                min={variant === "question" ? 12 : 40}
                max={5000}
                value={draft.max_words}
                onChange={(e) => setDraft((d) => ({ ...d, max_words: e.target.value }))}
                className="w-full bg-white border border-[#D8D6D2] rounded-md px-2.5 py-1.5 text-xs tabular-nums focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]"
                data-testid={`${testidPrefix}-${variant}-custom-max`}
              />
            </div>
          </div>
          <p className="text-[10px] text-[#6B6A68] leading-snug">
            Shapes the prompt instruction — doesn't cap tokens. The LLM still gets its full thinking budget.
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={saveCustom}
              className="flex-1 text-xs px-3 py-1.5 rounded-md bg-[#4A5D4E] text-[#FDFCF8] hover:bg-[#3A4D3E]"
              data-testid={`${testidPrefix}-${variant}-custom-save`}
            >
              Save preset
            </button>
            {preset === "custom" && custom && (
              <button
                type="button"
                onClick={deleteCustom}
                className="text-xs px-3 py-1.5 rounded-md bg-white border border-[#D8D6D2] text-[#A35757] hover:bg-[#FBE8E8] hover:border-[#A35757] flex items-center gap-1"
                data-testid={`${testidPrefix}-${variant}-custom-editor-delete`}
              >
                <X size={10} /> Delete
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default StyleSelector;
